import React from 'react'
import { CButton, CCol, CRow } from '@coreui/react-pro'
import { dateUSFormat, twelveHourTimeWithZone } from '../../../utils/dateTimeUtils'
import { getPCCResultFile } from '../../../helper/pccApiHelper'
import moment from 'moment'
import { getFusion } from '../../../apiHelper/fusionApi'

export function PatientInsuranceDisplay({ insuranceInfoData, DisplayFunction }) {
  return (
    <>
      {insuranceInfoData?.coverages?.payers?.map((info, index) => (
        <div key={index}>
          <h5 className="mt-3">{info.payerRank || '-'} Insurance</h5>
          <CRow>
            <CCol>
              <DisplayFunction title={'Insurance Name'} value={info?.payerName || '-'} />
            </CCol>
            <CCol>
              <DisplayFunction
                title={'Issuer'}
                value={
                  <div className={'d-flex flex-column'}>
                    <span>{info.issuer?.issuerName}</span>
                    <span>{info?.issuer?.issuerAddress?.addressLine1 || '-'}</span>
                    <span>{info?.issuer?.issuerAddress?.addressLine2 || ''}</span>
                    <span>
                      {info?.issuer?.issuerAddress?.issuerAddress?.city || ''}{' '}
                      {info?.issuer?.issuerAddress?.issuerAddress?.state || ''}{' '}
                      {info?.issuer?.issuerAddress?.issuerAddress?.postalCode || ''}
                    </span>
                  </div>
                }
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol>
              <DisplayFunction
                title={'Insured Party'}
                value={
                  <div className={'d-flex flex-column'}>
                    <span>
                      {info.insuredParty?.firstName} {info.insuredParty?.middleName}
                      {info.insuredParty?.lastName}
                    </span>
                    <span>{info?.insuredParty?.address?.addressLine1 || '-'}</span>
                    <span>{info?.insuredParty?.address?.addressLine2 || ''}</span>
                    <span>
                      {info?.insuredParty?.address?.city || ''}{' '}
                      {info?.insuredParty?.address?.state || ''}{' '}
                      {info?.insuredParty?.address?.postalCode || ''}
                    </span>
                  </div>
                }
              />
            </CCol>
            <CCol>
              <DisplayFunction
                title={'Plan Effective Date'}
                value={
                  info.issuer?.planEffectiveDate
                    ? dateUSFormat(info.issuer?.planEffectiveDate)
                    : '-'
                }
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol>
              <DisplayFunction
                title={'Plan Expiration Date'}
                value={
                  info.issuer?.planExpirationDate
                    ? dateUSFormat(info.issuer?.planExpirationDate)
                    : '-'
                }
              />
            </CCol>
            <CCol>
              <DisplayFunction
                title={'Plan Number'}
                value={
                  info.issuer?.planNumber || insuranceInfoData?.medicareBeneficiaryIdentifier || '-'
                }
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol>
              <DisplayFunction title={'Group Number'} value={info.issuer?.group || '-'} />
            </CCol>
            <CCol>
              <DisplayFunction title={'Group Name'} value={info.issuer?.groupName || '-'} />
            </CCol>
          </CRow>
          <CRow>
            <CCol>
              <DisplayFunction
                title={'Relationship'}
                value={info.insuredParty?.relationship || '-'}
              />
            </CCol>
            {info.payerRank === 'Primary' && (
              <CCol>
                <DisplayFunction title={'Gender'} value={info.insuredParty?.gender || '-'} />
              </CCol>
            )}
          </CRow>
          {info.payerRank === 'Primary' && (
            <CRow>
              <CCol>
                <DisplayFunction
                  title={'DOB'}
                  value={
                    info.insuredParty?.birthDate ? dateUSFormat(info.insuredParty.birthDate) : '-'
                  }
                />
              </CCol>
              <CCol>
                <DisplayFunction
                  title={'Social Beneficiary #'}
                  value={
                    info.insuredParty?.socialBeneficiaryIdentifier ||
                    insuranceInfoData?.socialBeneficiaryIdentifier ||
                    '-'
                  }
                />
              </CCol>
            </CRow>
          )}
          <hr />
        </div>
      ))}
    </>
  )
}

export function PatientClinicalDisplay({ clinicalInfoData, DisplayFunction }) {
  return (
    <>
      <h5 className="mt-3">Vitals</h5>
      {clinicalInfoData?.observation?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <DisplayFunction
              title={info[0]?.type.split(/(?=[A-Z])/).join(' ')}
              value={
                <>
                  {info[0]?.type !== 'bloodPressure'
                    ? info[0]?.value
                    : info[0].systolicValue + '/' + info[0].diastolicValue}
                  <span className="ms-2">{info[0].unit}</span>
                </>
              }
            />
          </CCol>
          <CCol>
            <DisplayFunction
              title={info[1]?.type.split(/(?=[A-Z])/).join(' ')}
              value={
                <>
                  {info[1]?.value}
                  <span className="ms-2">{info[1]?.unit}</span>
                </>
              }
            />
          </CCol>
        </CRow>
      ))}

      <h5 className="mt-3">Medical Diagnosis</h5>
      {clinicalInfoData?.medDiagnosis?.map((info, index) => (
        <CRow key={index} className="justify-content-start">
          <small className="medDiagnosis-subtitle">
            {info?.clinicalStatus} &middot; {info?.rankDescription}
          </small>
          <p className="medDiagnosis-description">
            {info?.icd10} <small>{info?.icd10Description}</small>
          </p>
        </CRow>
      ))}
      <h5 className="mt-3">Allergies</h5>
      {clinicalInfoData?.allergies?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <DisplayFunction
              title={
                <>
                  {info[0]?.type.split(/(?=[A-Z])/).join(' ')} &middot; {info[0]?.category} &middot;{' '}
                  {info[0]?.clinicalStatus}
                </>
              }
              value={
                <>
                  {info[0]?.allergen}
                  <span className="ms-2">{info[0].unit}</span>
                </>
              }
            />
            <p className="p-card-subtitle">
              {info[0]?.severity} {info[0]?.reactionType}
            </p>
          </CCol>
          <CCol>
            <DisplayFunction
              title={
                <>
                  {info[1]?.type.split(/(?=[A-Z])/).join(' ')} &middot; {info[1]?.category} &middot;{' '}
                  {info[1]?.clinicalStatus}
                </>
              }
              value={
                <>
                  {info[1]?.allergen}
                  <span>{info[1]?.unit}</span>
                </>
              }
            />
            <p className="p-card-subtitle">
              {info[1]?.severity} {info[1]?.reactionType}
            </p>
          </CCol>
        </CRow>
      ))}

      <h5 className="mt-3">Immunizations</h5>
      {clinicalInfoData?.immunizations?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <DisplayFunction
              title={
                <>
                  {info[0]?.given} {info[0]?.consentStatus}
                </>
              }
              value={info[0]?.immunization}
            />
          </CCol>
          <CCol>
            <DisplayFunction
              title={
                <>
                  {info[1]?.given} {info[1]?.consentStatus}
                </>
              }
              value={info[1]?.immunization}
            />
          </CCol>
        </CRow>
      ))}
    </>
  )
}

export function PatientLabRadiologyDisplay({ labsAndRadioInfoData, patientId, patient }) {
  return (
    <>
      <h5>Radiology</h5>
      {labsAndRadioInfoData?.radiology?.length === 0 && (
        <p className="labs-and-radio-description">No results found...</p>
      )}
      {labsAndRadioInfoData?.radiology?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <p className="labs-and-radio-subtitle">
              {info?.reportName || '-'} &middot; {info?.reportStatus || '-'} &middot;
              {info?.issuedDateTime ? twelveHourTimeWithZone(info?.issuedDateTime) : '-'}
            </p>
            {info.reportFile && (
              <CButton
                color="secondary"
                size="sm"
                className="ms-2 mb-2"
                onClick={() => onPCCDownload(patientId, info)}
              >
                <button>
                  <i
                    style={{ fontSize: '1.2em', marginRight: '4px' }}
                    className="fa-light fa-cloud-arrow-down"
                  ></i>
                  Download
                </button>
              </CButton>
            )}
            <CButton
              color="secondary"
              size="sm"
              className="ms-2 mb-2"
              onClick={() => onPCCViewReport(info, patient)}
            >
              <button>
                <i
                  style={{ fontSize: '1.2em', marginRight: '4px' }}
                  className="fa-light fa-file-lines"
                ></i>
                View Report
              </button>
            </CButton>
          </CCol>
        </CRow>
      ))}
      <h5>Laboratory</h5>
      {labsAndRadioInfoData?.laboratory?.length === 0 && (
        <p className="labs-and-radio-description">No results found...</p>
      )}
      {labsAndRadioInfoData?.laboratory?.map((info, index) => (
        <CRow key={index}>
          <CCol>
            <p className="labs-and-radio-subtitle">
              {info?.reportName || '-'} &middot; {info?.reportStatus || '-'} &middot;
              {info?.issuedDateTime ? twelveHourTimeWithZone(info?.issuedDateTime) : '-'}
            </p>
            {info.reportFile && (
              <CButton
                color="secondary"
                size="sm"
                className="ms-2 mb-2"
                onClick={() => onPCCDownload(patientId, info)}
              >
                <button>
                  <i
                    style={{ fontSize: '1.2em', marginRight: '4px' }}
                    className="fa-light fa-cloud-arrow-down"
                  ></i>
                  Download
                </button>
              </CButton>
            )}
            <CButton
              color="secondary"
              size="sm"
              className="ms-2 mb-2"
              onClick={() => onPCCViewReport(info, patient)}
            >
              <button>
                <i
                  style={{ fontSize: '1.2em', marginRight: '4px' }}
                  className="fa-light fa-file-lines"
                ></i>
                View Report
              </button>
            </CButton>
          </CCol>
        </CRow>
      ))}
    </>
  )
}

export function PatientMedDisplay({ medInfoData, DisplayFunction }) {
  return (
    <>
      {medInfoData?.map((med, index) => {
        if (med.directions === 'scrubbed') {
          return null
        }

        const schedule = med.schedules?.[0]

        return (
          <CRow key={'patmed-' + index}>
            <CRow>
              <CCol>
                <DisplayFunction
                  title={'Medication'}
                  value={med.description === 'scrubbed' ? med.generic : med.description}
                />
              </CCol>
              <CCol>
                <DisplayFunction title={'Status'} value={med.status} />
              </CCol>
              <CCol>
                <DisplayFunction
                  title={'Start Date'}
                  value={moment(med.startDate).format('MM/DD/yyyy')}
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol>
                <DisplayFunction
                  title={'Strength/Dosage'}
                  value={(med.strength || '') + (med.strengthUOM || '')}
                />
              </CCol>
              <CCol>
                <DisplayFunction
                  title={'Route'}
                  value={med.administration?.route?.coding?.[0]?.display}
                />
              </CCol>
              {schedule && (
                <CCol>
                  <DisplayFunction
                    title={'Frequency'}
                    value={`${schedule.dose || ''} ${schedule.doseUOM || ''} ${schedule.frequency}, ${schedule.scheduleType}`.trim()}
                  />
                </CCol>
              )}
            </CRow>
            {index < medInfoData.length - 2 && <hr />}
          </CRow>
        )
      })}
    </>
  )
}

const MIME_EXT = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/tiff': 'tiff',
}

export function onPCCDownload(patientId, info) {
  getPCCResultFile(patientId, info?.reportId).then((res) => {
    // getFusion resolves to response.data: a Blob on success,
    // or { status: 'failure', ... } on error (e.g. 404). Type-check for a real file.
    if (!(res instanceof Blob) || res.size === 0) {
      console.error('PCC download failed or returned no file')
      return
    }

    const contentType = res.type || 'application/pdf'
    const ext = MIME_EXT[contentType] || 'bin'

    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(res) // res is already a correctly-typed Blob

    const baseName = info?.reportName || info?.reportId || 'report'
    const fileName = /\.[a-z0-9]+$/i.test(baseName) ? baseName : `${baseName}.${ext}`
    link.setAttribute('download', fileName)

    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(link.href)
  })
}

// Escape values before interpolating report data into the HTML string we write to the new tab, so report contents can never inject markup or script.
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function buildReportHtml(info, patient) {
  // Patient shape differs by caller: PatientDetails passes the formatted object
  // (patient_firstname/...), the side panel passes the raw /patients/v2/ object
  // (first_name/...). Accept either.
  const patientName = [
    patient?.patient_firstname || patient?.first_name,
    patient?.patient_middlename || patient?.middle_name,
    patient?.patient_lastname || patient?.last_name,
  ]
    .filter(Boolean)
    .join(' ')
  const patientDob = patient?.patient_dob || patient?.dob
  const patientDobDisplay =
    patientDob && patientDob !== 'Invalid date' ? dateUSFormat(patientDob) : null
  const meta = [
    ['Patient', patientName],
    ['DOB', patientDobDisplay],
    ['Report Name', info?.reportName],
    ['Type', info?.reportType],
    ['Status', info?.reportStatus],
    ['Category', Array.isArray(info?.category) ? info.category.join(', ') : info?.category],
    ['Effective', info?.effectiveDateTime ? twelveHourTimeWithZone(info.effectiveDateTime) : null],
    ['Issued', info?.issuedDateTime ? twelveHourTimeWithZone(info.issuedDateTime) : null],
    ['Reporting Lab', info?.reportingLaboratory],
    ['Performing Lab', info?.performingLaboratory],
    [
      'Ordering Practitioner',
      typeof info?.orderingPractitioner === 'string' ? info.orderingPractitioner : null,
    ],
  ]
  const metaRows = meta
    .filter(([, value]) => value != null && value !== '')
    .map(([label, value]) => `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`)
    .join('')

  const panels = Array.isArray(info?.testSet) ? info.testSet : []
  const panelHtml = panels
    .map((panel) => {
      const results = Array.isArray(panel?.results) ? panel.results : []
      const rows = results
        .map((result) => {
          const observation =
            result?.observationName || result?.codeDescription || result?.code || '-'
          const value = result?.valueQuantity
            ? [
                result.valueQuantity.value,
                result.valueQuantity.unitText || result.valueQuantity.ucumUnitText,
              ]
                .filter(Boolean)
                .join(' ')
            : result?.valueString
          return `<tr>
            <td>${escapeHtml(observation)}</td>
            <td>${escapeHtml(value || '-')}</td>
            <td>${escapeHtml(result?.referenceRange ?? '-')}</td>
            <td>${escapeHtml(result?.resultStatus ?? '-')}</td>
            <td>${escapeHtml(result?.comment ?? '')}</td>
          </tr>`
        })
        .join('')
      return `<section class="panel">
        <h2>${escapeHtml(panel?.panelName || 'Results')}</h2>
        <table class="results">
          <thead><tr><th>Observation</th><th>Result</th><th>Reference Range</th><th>Status</th><th>Comment</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </section>`
    })
    .join('')

  const title = escapeHtml(info?.reportName || 'Report')
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${title}</title>
<style>
  body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #1a1a1a; margin: 24px; line-height: 1.4; }
  h1 { font-size: 1.4rem; margin: 0 0 16px; }
  h2 { font-size: 1.1rem; margin: 24px 0 8px; }
  table { border-collapse: collapse; width: 100%; margin-bottom: 8px; }
  table.meta th { text-align: left; width: 200px; color: #555; font-weight: 600; vertical-align: top; }
  table.meta th, table.meta td { padding: 4px 8px; }
  table.results th, table.results td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; vertical-align: top; font-size: 0.9rem; }
  table.results thead th { background: #f4f5f7; }
  details { margin-top: 24px; }
  summary { cursor: pointer; font-weight: 600; }
  pre { background: #f4f5f7; padding: 16px; border-radius: 6px; overflow: auto; font-size: 0.8rem; }
</style>
</head>
<body>
  <h1>${title}</h1>
  <table class="meta"><tbody>${metaRows}</tbody></table>
  ${panelHtml}
  <details ${panels.length ? '' : 'open'}>
    <summary>Full report data (JSON)</summary>
    <pre>${escapeHtml(JSON.stringify(info, null, 2))}</pre>
  </details>
</body>
</html>`
}

// Reports without a downloadable file carry their contents inline; render that JSON as a readable page in a new tab.
export function onPCCViewReport(info, patient) {
  const reportWindow = window.open('', '_blank')
  if (!reportWindow) {
    console.error('Unable to open report tab (popup blocked?)')
    return
  }
  reportWindow.document.write(buildReportHtml(info, patient))
  reportWindow.document.close()
}

export function CarryOverIcon({ visit, currentPatientAdmitDate }) {
  const isCarryover =
    visit?.admitPatientEvent?.charge_id &&
    visit.admitPatientEvent.charge_id !== visit._id &&
    !currentPatientAdmitDate

  return (
    isCarryover && (
      <i
        title={
          "When Admission Date is not received from an integration or manually entered, Claimocity's analytics predicts the date based on available data"
        }
        className="fa-regular fa-circle-info"
        style={{ color: 'var(--default-text-color)', marginTop: '2px' }}
      ></i>
    )
  )
}

export function DischargeIcon({ visit }) {
  const shouldShow = visit?.dischargePatientEvent?.source === 'COL-API'

  return (
    shouldShow && (
      <i
        title={
          "When Discharge Date is not received from an integration or manually entered, Claimocity's analytics predicts the date based on available data"
        }
        className="fa-regular fa-circle-info"
        style={{ color: 'var(--default-text-color)', marginTop: '2px', pointerEvents: 'auto' }}
      ></i>
    )
  )
}

// dont send an entire patient to the backend
// only send the fields needed
// uses `router.post("/visits"` as a reference
export function trimPatientObject(patient = {}) {
  let {
    amendments,
    dob,
    ethnicity,
    gender,
    genderIdentity,
    patient_lastname,
    patient_firstname,
    patient_middlename,
    preferredLanguage,
    primary_care_physician,
    race,
    sexualOrientation,
  } = { ...patient }
  return {
    amendments,
    dob,
    ethnicity,
    gender,
    genderIdentity,
    patient_lastname,
    patient_firstname,
    patient_middlename,
    preferredLanguage,
    primary_care_physician,
    race,
    sexualOrientation,
  }
}

export async function getPCPObject(pcp_id) {
  if (!pcp_id) {
    return
  }

  try {
    return await getFusion(`v1/patient/pcp/${pcp_id}`)
  } catch (e) {
    console.error(e)
    return ''
  }
}
